import { Button, message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./style.scss";
import { usePostData } from "../../../../Network/usePostData";
import { usePutData } from "../../../../Network/usePutData";
import { useSelector } from "react-redux";
import { useGetData } from "../../../../Network/useGetQueryData";
import { groupByProvider } from "../../../../utils/groupProvidersData";
import { Loading } from "../../../../components/Loading";
import { useTranslation } from "react-i18next";
import { UserRolesId } from "../../../enum";

export const SummaryStep = ({
  onChangeStep,
  formData,
  setFormData,
}: {
  onChangeStep?: (step: number) => void;
  formData: any;
  setFormData: any;
}) => {
  const { t, i18n } = useTranslation();
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState<boolean>();
  const [providersData, setProviders] = useState<any>();

  const { mutateAsync } = usePostData({
    url: "service-request",
    options: {
      onSuccess: () => {
        navigate("/requests");
      },
      onError: (error) => {
        navigate("/requests");
      },
    },
  });

  const userInfo = useSelector((state: any) => state.user.user) || {};
  const isProvider = userInfo?.roleId === UserRolesId.Provider;
  const { data: serviceProviders } = useGetData<{
    data: any;
  }>({
    queryKey: [
      "service-provider-users",
      formData.servicesIds,
      userInfo?.serviceProviderId,
    ],
    url: `service-provider-services`,
    params: {
      withDeleted: false,
      serviceProviderId: userInfo?.serviceProviderId,
      serviceIds: formData?.servicesIds.join(","),
    },
    options: {
      enabled:
        !!formData?.servicesIds && userInfo?.roleId === UserRolesId.Provider,
      queryKey: [
        "service-provider-users",
        formData.servicesIds,
        userInfo?.serviceProviderId,
      ],
    },
  });

  const { mutateAsync: calculatePrice } = usePostData({
    url: "service-request/calculate-prices",
    options: {
      onSuccess: () => {},
      onError: (error) => {
        //@ts-ignore
        message.error(error?.response?.data?.message);
      },
    },
  });

  const handleCalculatePrice = async () => {
    const filteredData = serviceProviders?.data?.items?.map((item: any) => {
      return {
        serviceProviderId: item?.serviceProviderId,
        serviceId: item?.serviceId,
      };
    });

    const response = (await calculatePrice({
      services: filteredData,
      customerId: formData?.customerId,
    })) as any;

    setProviders(response?.data);
  };

  useEffect(() => {
    if (!serviceProviders) return;
    handleCalculatePrice();
  }, [serviceProviders]);

  const updateSummaryProvider = useCallback(() => {
    if (userInfo?.roleId !== UserRolesId.Provider) return;
    const provider = groupByProvider(serviceProviders?.data?.items)?.[0];
    if (provider) {
      setFormData({
        ...formData,
        summary: provider?.services?.map((ser: any) => {
          return {
            ...ser,
            serviceProvider: provider,
          };
        }),
      });
    }
  }, [serviceProviders?.data?.items, formData?.serviceIds]);

  useEffect(() => {
    if (serviceProviders?.data && serviceProviders?.data?.items.length > 0)
      updateSummaryProvider();
  }, [serviceProviders?.data?.items]);

  const { mutateAsync: addServices } = usePostData({
    url: "service-request/services",
    options: {
      onSuccess: () => {
        navigate("/requests");
      },
      onError: (error) => {
        //@ts-ignore
        message.error(error?.response?.data?.message);
      },
    },
  });

  const totalPrice = isProvider
    ? providersData?.totalAmountAfterDiscount
    : formData?.summary?.[0]?.serviceProvider?.totalPrice;
  const totalPriceBefore = isProvider
    ? providersData?.totalAmount
    : formData?.summary?.[0]?.serviceProvider?.totalPriceBefore;

  const medHubPrice = isProvider
    ? providersData?.totalCoveragesAmount
    : formData?.summary?.[0]?.serviceProvider?.totalMedHubPrice;

  const handleAddRequest = async () => {
    setBtnLoading(true);
    const values = {
      services: formData?.summary?.map((ser: any) => {
        return {
          serviceId: ser?.id,
          serviceProviderId: ser?.serviceProvider?.serviceProvider?.id,
        };
      }),
      paymentMethod: "cash",
      customerId: formData?.customerId,
      requestedBy:
        formData?.requestedBy ||
        (userInfo?.roleId === UserRolesId.Provider
          ? "serviceProvider"
          : userInfo?.roleId === UserRolesId.Sponsor
            ? "customer"
            : "admin"),
      type: "withServices",
    };
    try {
      if (requestId)
        addServices({
          services: formData?.summary?.map((ser: any) => {
            return {
              serviceId: ser?.id,
              serviceProviderId: ser?.serviceProvider?.serviceProvider?.id,
            };
          }),
          serviceRequestId: +requestId,
        });
      else await mutateAsync(values);
      setBtnLoading(false);
    } catch (error) {
      setBtnLoading(false);
    }
  };

  return (
    <>
      <div className="request-summary-wrapper">
        <p>{t("summary.title")}</p>

        {formData?.summary ? (
          <>
            <div className="request-services-container">
              {formData?.summary?.map((service: any) => (
                <div key={service?.id} className="request-services-wrapper">
                  <div className="service-breakdown-wrapper">
                    <p className="service-name">
                      {service.name[i18n.language || "en"]}
                    </p>
                    <div className="service-provider-service-wrapper">
                      <span>
                        <img
                          src={
                            service?.serviceProvider?.serviceProvider?.logoUrl
                          }
                          alt="logo-url"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/assets/providers/mokhtabar.png";
                          }}
                        />
                      </span>
                      <p>
                        {
                          service?.serviceProvider?.serviceProvider?.name[
                            i18n.language || "en"
                          ]
                        }
                        <span>{service?.priceBefore}</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="total-section">
              <p>{t("summary.total")}</p>
              <div>
                <Button onClick={() => onChangeStep?.(1)} type="link">
                  {t("summary.addMore")}
                </Button>
                <span>{totalPriceBefore?.toFixed(2)} L.E.</span>
              </div>
            </div>
            <div className="total-section med-hub">
              <p>{t("summary.medHubShare")}</p>
              <div>
                <span>{medHubPrice?.toFixed(2)} L.E.</span>
              </div>
            </div>
            <div className="total-section client">
              <p>{t("summary.clientShare")}</p>
              <div>
                <span>{totalPrice?.toFixed(2)} L.E.</span>
              </div>
            </div>
          </>
        ) : (
          <Loading />
        )}
      </div>

      <div className="steps-action-footer">
        <Button
          style={{ marginRight: "8px" }}
          onClick={() => onChangeStep?.(2)}
        >
          {t("buttons.previous")}
        </Button>
        <Button
          type="primary"
          onClick={async () => {
            try {
              handleAddRequest();
            } catch (error) {}
          }}
          loading={btnLoading}
        >
          {t("buttons.finish")}
        </Button>
      </div>
    </>
  );
};
